/**
 * Open question reports (brief §teacher: `/teacher/reports`) with a resolve
 * button — students file these through `<ReportIssue />` in every feature
 * that shows a question (spec §4.1 content).
 */
import { useState } from "react";
import { Button, Card, EmptyState, PageHeader, Spinner, Tabs } from "@client/components/ui";
import { useToast } from "@client/components/ui";
import { useLocalizedText } from "@client/components/Localized";
import { useLang, useT } from "@client/i18n";
import { formatDate } from "@client/lib/format";
import { messageForError } from "@client/lib/errors";
import { previewStatement } from "@client/lib/preview";
import { TeacherNav } from "./TeacherNav";
import { useResolveReport, useTeacherReports } from "./api";
import type { QuestionReport, ReportStatus } from "./types";

function ReportCard({ report }: { report: QuestionReport }) {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const toast = useToast();
  const resolve = useResolveReport();
  // No dictionary entry fits "question no longer exists" — the id is the
  // next-best identifier for the teacher.
  const preview = report.statement
    ? previewStatement(localize(report.statement))
    : report.questionId;

  return (
    <Card compact>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-ink-500 dark:text-ink-400">
            {report.questionId} · {formatDate(report.createdAt, lang)} ·{" "}
            {report.studentName ?? t("teacher.reports.anonymous")}
          </p>
          <p className="mt-1 text-sm text-ink-700 dark:text-ink-200">{preview}</p>
          <p className="mt-2 text-sm text-ink-900 dark:text-ink-50">{report.note}</p>
        </div>
        {report.status === "open" && (
          <Button
            variant="secondary"
            size="sm"
            loading={resolve.isPending}
            onClick={async () => {
              try {
                await resolve.mutateAsync(report.id);
              } catch (error) {
                toast.error(messageForError(error, t));
              }
            }}
          >
            {t("teacher.reports.resolve")}
          </Button>
        )}
      </div>
    </Card>
  );
}

export function TeacherReportsPage() {
  const t = useT();
  const [status, setStatus] = useState<ReportStatus>("open");
  const query = useTeacherReports(status);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={t("teacher.reports.title")}
        description={t("teacher.reports.description")}
      />
      <TeacherNav />

      <Tabs
        ariaLabel={t("teacher.reports.title")}
        value={status}
        onChange={(id) => setStatus(id as ReportStatus)}
        items={[
          { id: "open", label: t("teacher.reports.tabs.open") },
          { id: "resolved", label: t("teacher.reports.tabs.resolved") },
        ]}
        className="mb-4"
      />

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {query.data && query.data.length === 0 && <EmptyState title={t("teacher.reports.empty")} />}

      {query.data && query.data.length > 0 && (
        <div className="space-y-3">
          {query.data.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
}

export default TeacherReportsPage;
